import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PAYMENT_MODES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader } from "@/components/ui-kit";
import { Wallet, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function PurchaseDetailDialog({ purchaseId, onOpenChange, onChanged }) {
  const [p, setP] = useState(null);
  const [modes, setModes] = useState(PAYMENT_MODES);
  const [pay, setPay] = useState({ montant: "", mode: "Espèces", date: new Date().toISOString().slice(0, 10), note: "" });

  const load = () => api.get("/purchases").then((r) => setP(r.data.find((x) => x.id === purchaseId) || null));
  useEffect(() => {
    if (purchaseId) { setP(null); load(); api.get("/settings").then((r) => r.data.payment_methods && setModes(r.data.payment_methods)).catch(() => {}); }
  }, [purchaseId]);

  const addPayment = async (e) => {
    e.preventDefault();
    const montant = Number(pay.montant);
    if (!montant || montant <= 0) { toast.error("Montant invalide"); return; }
    try {
      const { data } = await api.post(`/purchases/${purchaseId}/payments`, { ...pay, montant });
      setP(data); setPay({ montant: "", mode: "Espèces", date: new Date().toISOString().slice(0, 10), note: "" });
      toast.success("Règlement enregistré"); onChanged && onChanged();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const rmPayment = async (payid) => {
    try { const { data } = await api.delete(`/purchases/${purchaseId}/payments/${payid}`); setP(data); onChanged && onChanged(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <Dialog open={!!purchaseId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="purchase-detail">
        {!p ? <Loader /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-3">
                <Wallet className="h-5 w-5 text-brand-600" />Achat {p.numero}
                <span className="font-normal text-sm text-slate-400">· {p.supplier_name}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg border bg-white p-3"><p className="text-[10px] uppercase font-mono text-slate-400">Total</p><p className="font-mono font-bold text-slate-800">{fmtMoney(p.total)}</p></div>
              <div className="rounded-lg border bg-emerald-50 p-3"><p className="text-[10px] uppercase font-mono text-emerald-600">Payé</p><p className="font-mono font-bold text-emerald-700">{fmtMoney(p.montant_paye)}</p></div>
              <div className="rounded-lg border bg-rose-50 p-3"><p className="text-[10px] uppercase font-mono text-rose-600">Dette</p><p className="font-mono font-bold text-rose-700">{fmtMoney(p.solde)}</p></div>
            </div>
            <div className="flex items-center gap-2"><span className="text-sm text-slate-500">État :</span><StatusBadge value={p.dette_etat} />{p.echeance && <span className="text-xs text-slate-400 ml-auto">Échéance : {fmtDate(p.echeance)}</span>}</div>

            <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mt-2">Règlements</p>
            <div className="space-y-1.5">
              {(p.payments || []).length === 0 ? <p className="text-sm text-slate-400">Aucun règlement</p> :
                p.payments.map((x) => (
                  <div key={x.id} className="flex items-center justify-between rounded-lg border bg-slate-50/60 px-3 py-2" data-testid={`ppayment-${x.id}`}>
                    <span className="text-xs text-slate-500 w-24">{fmtDate(x.date)}</span>
                    <span className="text-sm text-slate-700 flex-1 px-2">{x.mode}{x.note ? ` · ${x.note}` : ""}</span>
                    <span className="font-mono text-sm font-semibold text-emerald-700">{fmtMoney(x.montant)}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 ml-2" onClick={() => rmPayment(x.id)} data-testid={`ppayment-del-${x.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                  </div>
                ))}
            </div>

            {p.solde > 0 && p.statut !== "Annulée" && (
              <form onSubmit={addPayment} className="rounded-lg border bg-white p-3 grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                <div><Label className="text-xs">Montant</Label><Input type="number" value={pay.montant} onChange={(e) => setPay({ ...pay, montant: e.target.value })} data-testid="ppayment-montant" className="mt-1 h-9" placeholder="0" /></div>
                <div><Label className="text-xs">Mode</Label>
                  <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}><SelectTrigger className="mt-1 h-9" data-testid="ppayment-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Date</Label><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className="mt-1 h-9" /></div>
                <Button type="submit" data-testid="ppayment-add" className="bg-brand-600 hover:bg-brand-700 text-white h-9"><Plus className="h-4 w-4 mr-1" />Régler</Button>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
